/**
 * 围棋音效管理器
 * 负责管理围棋对弈中的各种音效
 */
export var SoundType;
(function (SoundType) {
    SoundType["STONE_PLACE"] = "stone_place";
    SoundType["STONE_PLACE_BLACK"] = "stone_place_black";
    SoundType["STONE_PLACE_WHITE"] = "stone_place_white";
    SoundType["STONE_CAPTURE"] = "stone_capture";
    SoundType["STONE_CLICK"] = "stone_click";
    SoundType["PASS"] = "pass";
    SoundType["RESIGN"] = "resign";
    SoundType["WIN"] = "win";
    SoundType["LOSE"] = "lose";
    SoundType["DRAW"] = "draw";
    SoundType["ERROR"] = "error";
    SoundType["NOTIFICATION"] = "notification"; // 通知音效
})(SoundType || (SoundType = {}));
export class SoundManager {
    constructor(config) {
        this.audioContext = null;
        this.sounds = new Map();
        this.isInitialized = false;
        this.config = {
            enabled: true,
            volume: 0.7,
            mute: false,
            ...config
        };
    }
    /**
     * 初始化音效管理器
     */
    async initialize() {
        if (this.isInitialized)
            return true;
        try {
            // 创建音频上下文
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            // 预加载音效
            await this.preloadSounds();
            this.isInitialized = true;
            return true;
        }
        catch (error) {
            console.warn('音效初始化失败:', error);
            return false;
        }
    }
    /**
     * 预加载音效
     */
    async preloadSounds() {
        if (!this.audioContext)
            return;
        const soundPromises = [
            this.loadStonePlaceSound('black'),
            this.loadStonePlaceSound('white'),
            this.loadStoneCaptureSound(),
            this.loadClickSound(),
            this.loadPassSound(),
            this.loadResignSound()
        ];
        await Promise.all(soundPromises);
    }
    /**
     * 加载落子音效（黑/白棋各一套，便于分辨谁落子）
     */
    async loadStonePlaceSound(color) {
        const buffer = this.generateStonePlaceSound(color);
        this.sounds.set(color === 'black' ? SoundType.STONE_PLACE_BLACK : SoundType.STONE_PLACE_WHITE, buffer);
        // 兼容旧调用：默认落子音用黑棋声
        if (color === 'black')
            this.sounds.set(SoundType.STONE_PLACE, buffer);
    }
    /**
     * 加载提子音效
     */
    async loadStoneCaptureSound() {
        // 生成提子音效（棋子落入棋盒的声音）
        const buffer = this.generateStoneCaptureSound();
        this.sounds.set(SoundType.STONE_CAPTURE, buffer);
    }
    /**
     * 加载点击音效
     */
    async loadClickSound() {
        const buffer = this.generateClickSound();
        this.sounds.set(SoundType.STONE_CLICK, buffer);
    }
    /**
     * 加载PASS音效
     */
    async loadPassSound() {
        const buffer = this.generatePassSound();
        this.sounds.set(SoundType.PASS, buffer);
    }
    /**
     * 加载认输音效
     */
    async loadResignSound() {
        const buffer = this.generateResignSound();
        this.sounds.set(SoundType.RESIGN, buffer);
    }
    /**
     * 生成落子音效（冲击脉冲 + 木盘共振，野狐式黑白分声）
     * 黑：基频320 / 主共振900 / 次1600,2400，区间200–3500Hz，Decay 150ms（厚重“喀”）
     * 白：基频420 / 主共振1200 / 次1900,2700，区间300–4000Hz，Decay 130ms（清亮“哒”）
     * 结构：极短冲击尖峰（1–3ms）→ 快速指数衰减的多共振峰；带通去低频浑浊与高频毛刺。
     */
    generateStonePlaceSound(color) {
        if (!this.audioContext)
            throw new Error('音频上下文未初始化');
        const sr = this.audioContext.sampleRate;
        const p = color === 'black'
            ? { base: 320, main: 900, h2: 1600, h3: 2400, lp: 3500, hp: 200, decay: 0.15, attack: 0.002, duration: 0.22 }
            : { base: 420, main: 1200, h2: 1900, h3: 2700, lp: 4000, hp: 300, decay: 0.13, attack: 0.002, duration: 0.20 };
        const frameCount = Math.floor(p.duration * sr);
        const buffer = this.audioContext.createBuffer(1, frameCount, sr);
        const data = buffer.getChannelData(0);
        // 共振峰相对幅度（主共振能量最强，基频次之，泛音递减）
        const partials = [
            { f: p.base, a: 0.55 },
            { f: p.main, a: 0.70 },
            { f: p.h2, a: 0.30 },
            { f: p.h3, a: 0.15 }
        ];
        const tau = p.decay / 3; // ~decay 时衰到 ~5%
        const impulseTau = 0.0015; // 冲击尖峰 ~1.5ms
        let prevNoise = 0;
        for (let i = 0; i < frameCount; i++) {
            const t = i / sr;
            // 冲击尖峰（宽带瞬态，形成“啪/喀”的起音）
            const w = Math.random() * 2 - 1;
            const impulse = (w - prevNoise) * Math.exp(-t / impulseTau) * 0.5;
            prevNoise = w;
            // 共振峰（阻尼正弦，形成棋子/木盘的音色）
            let res = 0;
            for (const q of partials) {
                res += Math.sin(2 * Math.PI * q.f * t) * q.a * Math.exp(-t / tau);
            }
            // 起音斜坡（~2ms）避免爆音
            const atk = Math.min(1, i / Math.max(1, sr * p.attack));
            data[i] = (impulse * 0.6 + res * 0.5) * atk;
        }
        // 带通：先低通（去 >lp），后高通（去 <hp），各两级 ≈ 12dB/oct
        this.applyBandpass(data, sr, p.hp, p.lp);
        // 归一化到 0.5 峰值（≈ -6dBFS）
        let max = 0;
        for (let i = 0; i < frameCount; i++)
            max = Math.max(max, Math.abs(data[i]));
        if (max > 0) {
            const g = 0.5 / max;
            for (let i = 0; i < frameCount; i++)
                data[i] *= g;
        }
        return buffer;
    }
    /** 一阶低通/高通滤波级联，去低频浑浊与高频毛刺（order 越大衰减越陡） */
    applyBandpass(data, sr, hp, lp, order = 2) {
        const dt = 1 / sr;
        // 低通
        const aLp = dt / (1 / (2 * Math.PI * lp) + dt);
        for (let p = 0; p < order; p++) {
            let y = 0;
            for (let i = 0; i < data.length; i++) {
                y += aLp * (data[i] - y);
                data[i] = y;
            }
        }
        // 高通
        const rcHp = 1 / (2 * Math.PI * hp);
        const aHp = rcHp / (rcHp + dt);
        for (let p = 0; p < order; p++) {
            let y = 0, xPrev = 0;
            for (let i = 0; i < data.length; i++) {
                const x = data[i];
                y = aHp * (y + x - xPrev);
                xPrev = x;
                data[i] = y;
            }
        }
    }
    /**
     * 生成提子音效（拿起一把棋子的“簌簌”声）
     * = 一串细碎高频瞬态颗粒（1000–5000Hz），总时长 ~400ms
     */
    generateStoneCaptureSound() {
        if (!this.audioContext)
            throw new Error('音频上下文未初始化');
        const sr = this.audioContext.sampleRate;
        const duration = 0.4; // 400ms
        const n = Math.floor(duration * sr);
        const buffer = this.audioContext.createBuffer(1, n, sr);
        const data = buffer.getChannelData(0);
        // 细碎颗粒簇：随机时刻叠加短促高频瞬态（前密后疏）
        const grains = 34;
        for (let g = 0; g < grains; g++) {
            const start = Math.floor(Math.pow(Math.random(), 1.25) * n * 0.9);
            const grainLen = Math.floor((0.003 + Math.random() * 0.010) * sr); // 3–13ms
            const decay = 0.002 + Math.random() * 0.005;
            const amp = 0.35 + Math.random() * 0.65;
            let prev = 0;
            for (let i = 0; i < grainLen; i++) {
                const idx = start + i;
                if (idx >= n)
                    break;
                const t = i / sr;
                const w = Math.random() * 2 - 1;
                const hp = w - prev;
                prev = w; // 差分提亮
                data[idx] += hp * Math.exp(-t / decay) * amp;
            }
        }
        // 带通 1000–5000Hz（4 级 ≈ 24dB/oct）
        this.applyBandpass(data, sr, 1000, 5000, 4);
        // 归一化到 0.4 峰值（比落子声轻）
        let max = 0;
        for (let i = 0; i < n; i++)
            max = Math.max(max, Math.abs(data[i]));
        if (max > 0) {
            const gain = 0.4 / max;
            for (let i = 0; i < n; i++)
                data[i] *= gain;
        }
        return buffer;
    }
    /**
     * 生成点击音效
     */
    generateClickSound() {
        if (!this.audioContext)
            throw new Error('音频上下文未初始化');
        const duration = 0.1; // 0.1秒
        const sampleRate = this.audioContext.sampleRate;
        const frameCount = Math.floor(duration * sampleRate);
        const buffer = this.audioContext.createBuffer(1, frameCount, sampleRate);
        const channelData = buffer.getChannelData(0);
        // 生成短促的点击声
        for (let i = 0; i < frameCount; i++) {
            const time = i / sampleRate;
            const envelope = Math.exp(-time * 50); // 快速衰减
            // 高频点击声
            const click = Math.sin(2 * Math.PI * 1000 * time) * envelope;
            channelData[i] = click * 0.5;
        }
        return buffer;
    }
    /**
     * 生成PASS音效
     */
    generatePassSound() {
        if (!this.audioContext)
            throw new Error('音频上下文未初始化');
        const duration = 0.4; // 0.4秒
        const sampleRate = this.audioContext.sampleRate;
        const frameCount = Math.floor(duration * sampleRate);
        const buffer = this.audioContext.createBuffer(1, frameCount, sampleRate);
        const channelData = buffer.getChannelData(0);
        // 生成柔和的提示音
        for (let i = 0; i < frameCount; i++) {
            const time = i / sampleRate;
            const envelope = Math.exp(-time * 10); // 缓慢衰减
            // 两个频率的合成
            const wave1 = Math.sin(2 * Math.PI * 523.25 * time) * 0.7; // C5
            const wave2 = Math.sin(2 * Math.PI * 659.25 * time) * 0.3; // E5
            channelData[i] = (wave1 + wave2) * envelope * 0.3;
        }
        return buffer;
    }
    /**
     * 生成认输音效
     */
    generateResignSound() {
        if (!this.audioContext)
            throw new Error('音频上下文未初始化');
        const duration = 0.8; // 0.8秒
        const sampleRate = this.audioContext.sampleRate;
        const frameCount = Math.floor(duration * sampleRate);
        const buffer = this.audioContext.createBuffer(1, frameCount, sampleRate);
        const channelData = buffer.getChannelData(0);
        // 生成低沉的音效
        for (let i = 0; i < frameCount; i++) {
            const time = i / sampleRate;
            const envelope = Math.exp(-time * 5); // 缓慢衰减
            // 低频下降音
            const frequency = 220 * Math.exp(-time * 2); // 从220Hz下降
            const wave = Math.sin(2 * Math.PI * frequency * time);
            channelData[i] = wave * envelope * 0.4;
        }
        return buffer;
    }
    /**
     * 播放音效
     */
    play(soundType, volume) {
        if (!this.config.enabled || this.config.mute || !this.isInitialized || !this.audioContext) {
            return;
        }
        const buffer = this.sounds.get(soundType);
        if (!buffer) {
            console.warn(`音效未找到: ${soundType}`);
            return;
        }
        try {
            const source = this.audioContext.createBufferSource();
            const gainNode = this.audioContext.createGain();
            source.buffer = buffer;
            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);
            // 设置音量
            const finalVolume = (volume !== undefined ? volume : 1.0) * this.config.volume;
            gainNode.gain.setValueAtTime(finalVolume, this.audioContext.currentTime);
            // 播放
            source.start();
        }
        catch (error) {
            console.warn(`播放音效失败: ${soundType}`, error);
        }
    }
    /**
     * 播放落子音效（按棋子颜色选声；不传则用黑棋声）
     */
    playStonePlace(color, volume) {
        const type = color === 'white' ? SoundType.STONE_PLACE_WHITE : SoundType.STONE_PLACE_BLACK;
        this.play(type, volume);
    }
    /**
     * 播放提子音效
     */
    playStoneCapture(numStones, volume) {
        // 根据提子数量调整音量
        let adjustedVolume = volume;
        if (numStones !== undefined) {
            adjustedVolume = (volume || 1.0) * Math.min(1.0, 0.3 + numStones * 0.1);
        }
        this.play(SoundType.STONE_CAPTURE, adjustedVolume);
    }
    /**
     * 播放点击音效
     */
    playClick(volume) {
        this.play(SoundType.STONE_CLICK, volume);
    }
    /**
     * 播放PASS音效
     */
    playPass(volume) {
        this.play(SoundType.PASS, volume);
    }
    /**
     * 播放认输音效
     */
    playResign(volume) {
        this.play(SoundType.RESIGN, volume);
    }
    /**
     * 播放胜利音效
     */
    playWin(volume) {
        // 暂时使用PASS音效替代
        this.play(SoundType.PASS, volume);
    }
    /**
     * 播放失败音效
     */
    playLose(volume) {
        // 暂时使用认输音效替代
        this.play(SoundType.RESIGN, volume);
    }
    /**
     * 播放错误音效
     */
    playError(volume) {
        // 暂时使用点击音效替代
        this.play(SoundType.STONE_CLICK, volume);
    }
    /**
     * 播放通知音效
     */
    playNotification(volume) {
        // 暂时使用PASS音效替代
        this.play(SoundType.PASS, volume);
    }
    /**
     * 更新配置
     */
    updateConfig(config) {
        this.config = { ...this.config, ...config };
    }
    /**
     * 启用/禁用音效
     */
    setEnabled(enabled) {
        this.config.enabled = enabled;
    }
    /**
     * 设置音量
     */
    setVolume(volume) {
        this.config.volume = Math.max(0, Math.min(1, volume));
    }
    /**
     * 静音/取消静音
     */
    setMute(mute) {
        this.config.mute = mute;
    }
    /**
     * 获取当前配置
     */
    getConfig() {
        return { ...this.config };
    }
    /**
     * 检查是否已初始化
     */
    isReady() {
        return this.isInitialized;
    }
    /**
     * 销毁音效管理器
     */
    destroy() {
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        this.sounds.clear();
        this.isInitialized = false;
    }
}
//# sourceMappingURL=SoundManager.js.map